import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import type { SgpIdBetRow, SgpIdBetStatus } from "./sgpIdBetsListData";
import {
  SGP_ID_BET_DATE_TIME_FORMAT,
  SGP_RISK_BETS_LIST_SORT_FIELDS,
  type SgpRiskBetsListSortBy,
} from "../../../../queries/sportsbook/sgpRiskBetsList";

dayjs.extend(customParseFormat);

export type SortField =
  | "id"
  | "player"
  | "dateTime"
  | "sport"
  | "league"
  | "sgpId"
  | "selection"
  | "odds"
  | "betResult"
  | "betAmount"
  | "status"
  | "payout";

export type SortOrder = "ascend" | "descend" | null;

const STATUS_SORT_RANK: Record<SgpIdBetStatus, number> = {
  Won: 0,
  Lost: 1,
  Pending: 2,
  Push: 3,
};

function compareText(left: string, right: string) {
  return left.localeCompare(right, undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

export function parseDateTime(value: string) {
  const parsed = dayjs(value, SGP_ID_BET_DATE_TIME_FORMAT, true);
  return parsed.isValid() ? parsed.valueOf() : Number.NEGATIVE_INFINITY;
}

export function normalizeSorter(sorter: unknown): {
  field: SortField | null;
  order: SortOrder;
} {
  const normalized = (Array.isArray(sorter) ? sorter[0] : sorter) as
    | {
        field?: unknown;
        columnKey?: unknown;
        order?: unknown;
      }
    | undefined;
  const field = normalized?.columnKey ?? normalized?.field;
  const order = normalized?.order;

  return {
    field: field ? (String(field) as SortField) : null,
    order: order === "ascend" || order === "descend" ? order : null,
  };
}

export function compareRowsByField(
  field: SortField,
  left: SgpIdBetRow,
  right: SgpIdBetRow,
) {
  switch (field) {
    case "id":
      return compareText(left.id, right.id);
    case "player":
      return compareText(left.player, right.player);
    case "dateTime":
      return parseDateTime(left.dateTime) - parseDateTime(right.dateTime);
    case "sport":
      return compareText(left.sport, right.sport);
    case "league":
      return compareText(left.league, right.league);
    case "sgpId":
      return compareText(left.sgpId, right.sgpId);
    case "selection":
      return compareText(
        left.selections.map(({ value }) => value).join(" | "),
        right.selections.map(({ value }) => value).join(" | "),
      );
    case "odds":
      return left.overallOddsValue - right.overallOddsValue;
    case "betResult":
      return compareText(
        left.selections.map(({ result }) => result).join(" | "),
        right.selections.map(({ result }) => result).join(" | "),
      );
    case "betAmount":
      return left.betAmount - right.betAmount;
    case "status":
      return STATUS_SORT_RANK[left.status] - STATUS_SORT_RANK[right.status];
    case "payout":
      return left.winAmount - right.winAmount;
    default:
      return 0;
  }
}

export type ServerSortChangeInput = {
  sorter: unknown;
  currentSortBy: SgpRiskBetsListSortBy;
  currentSortOrder: "asc" | "desc";
};

export type ServerSortChangeResult =
  | { type: "noop" }
  | { type: "reset"; sortBy: "created_at"; sortOrder: "desc" }
  | { type: "update"; sortBy: SgpRiskBetsListSortBy; sortOrder: "asc" | "desc" };

export function resolveServerSortChange({
  sorter,
  currentSortBy,
  currentSortOrder,
}: ServerSortChangeInput): ServerSortChangeResult {
  const activeSorter = normalizeSorter(sorter);
  const sortKey = activeSorter.field;
  const nextSortBy = sortKey
    ? SGP_RISK_BETS_LIST_SORT_FIELDS[
        sortKey as keyof typeof SGP_RISK_BETS_LIST_SORT_FIELDS
      ]
    : undefined;

  if (!nextSortBy || !activeSorter.order) {
    if (
      sortKey &&
      SGP_RISK_BETS_LIST_SORT_FIELDS[
        sortKey as keyof typeof SGP_RISK_BETS_LIST_SORT_FIELDS
      ] &&
      !activeSorter.order
    ) {
      return { type: "reset", sortBy: "created_at", sortOrder: "desc" };
    }

    return { type: "noop" };
  }

  const nextSortOrder = activeSorter.order === "ascend" ? "asc" : "desc";

  if (nextSortBy === currentSortBy && nextSortOrder === currentSortOrder) {
    return { type: "noop" };
  }

  return { type: "update", sortBy: nextSortBy, sortOrder: nextSortOrder };
}
